// Exporting the backlog to Excel.  tickets[9304]
//
// Building the sheet is pure and separate from writing the file: the rows are
// what a test can pin (labels, never ids; dates that read the way the screen
// shows them), and the writer is a thin, lazily loaded wrapper, so the
// spreadsheet library costs nothing until someone actually clicks Export.

import type { BacklogItem } from "@/hooks/useBacklogItems";
import type { DropdownOption, UserData } from "@/types/backlog";
import { getDisplayName } from "@/lib/userUtils";
import { pipelineProgress } from "@/lib/pipelines";
import { richContentToPlainText } from "@/lib/legacyToHtml";
import { formatFieldValue, type CustomField } from "@/lib/customFields";

/** Excel refuses a cell longer than this many characters. */
export const EXCEL_CELL_LIMIT = 32767;

/** What the export needs to turn ids into the names people read. */
export interface ExportLookups {
  /** Every dropdown option (statuses, priorities, types) — dropdownData.allOptions. */
  options: readonly DropdownOption[];
  profiles: readonly Pick<UserData, "id" | "first_name" | "last_name" | "email">[];
  subsheets: readonly { id: string; name: string }[];
  teams: readonly { id: string; name: string }[];
  /** Pipelines, for the Pipeline and Step columns (#9304). Optional: no pipelines, empty columns. */
  pipelines?: readonly { id: string; name: string; statusIds: readonly string[] }[];
  /**
   * Custom fields to add as columns, in order (#9399): the ones that apply to
   * the exported tickets. A number stays a number and a date a date; the rest
   * export as the label a person reads.
   */
  customFields?: readonly CustomField[];
}

export type ExportCell = string | number | Date | null;

export interface ExportColumn {
  header: string;
  /** Character width of the column in Excel. */
  width: number;
  /** Excel number format, for date columns. */
  format?: string;
}

export interface ExportSheet {
  columns: ExportColumn[];
  rows: ExportCell[][];
}

const UNKNOWN = "Unknown";

/**
 * Plain text from stored rich text, cut to fit a cell. richContentToPlainText
 * is the clipboard's copy of the rule (TipTap HTML and the legacy link/mention
 * format alike); this only adds Excel's length limit.
 */
export function htmlToText(content: string): string {
  return richContentToPlainText(content).slice(0, EXCEL_CELL_LIMIT);
}

/**
 * A Date whose UTC fields hold the local wall-clock time of `iso`. The writer
 * serialises a Date by its UTC instant, so without this shift a ticket filed
 * at 9am in New York would read 1pm (or 2pm) in the spreadsheet.
 */
const localWallClock = (iso: string): Date => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
};

/** A bare YYYY-MM-DD is a calendar day, not an instant: midnight UTC, no shift. */
const calendarDay = (ymd: string): Date => new Date(`${ymd}T00:00:00Z`);

const joinOrNull = (values: string[]): string | null => (values.length > 0 ? values.join(", ") : null);

export function buildExportSheet(items: readonly BacklogItem[], lookups: ExportLookups): ExportSheet {
  const optionLabel = new Map(lookups.options.map((o) => [o.id, o.label]));
  const personName = new Map(lookups.profiles.map((p) => [p.id, getDisplayName(p)]));
  const subsheetName = new Map(lookups.subsheets.map((s) => [s.id, s.name]));
  const teamName = new Map(lookups.teams.map((t) => [t.id, t.name]));
  const pipelineById = new Map((lookups.pipelines ?? []).map((p) => [p.id, p]));
  const step = (i: BacklogItem): string | null => {
    const pipeline = i.pipeline_id ? pipelineById.get(i.pipeline_id) : undefined;
    const progress = pipeline ? pipelineProgress(pipeline.statusIds, i.status_id) : null;
    return progress ? `${progress.index + 1} of ${progress.total} (${progress.percent}%)` : null;
  };

  const label = (id: string | null) => (id ? (optionLabel.get(id) ?? UNKNOWN) : null);
  const names = (ids: readonly string[] | null | undefined, from: Map<string, string>) =>
    joinOrNull((ids ?? []).map((id) => from.get(id) ?? UNKNOWN));

  const columns: Array<ExportColumn & { cell: (item: BacklogItem) => ExportCell }> = [
    { header: "#", width: 8, cell: (i) => i.ticket_number },
    { header: "Title", width: 48, cell: (i) => i.task },
    { header: "Status", width: 16, cell: (i) => label(i.status_id) },
    {
      header: "Pipeline",
      width: 18,
      cell: (i) => (i.pipeline_id ? (pipelineById.get(i.pipeline_id)?.name ?? UNKNOWN) : null),
    },
    { header: "Step", width: 14, cell: step },
    { header: "Priority", width: 12, cell: (i) => label(i.priority_id) },
    { header: "Type", width: 14, cell: (i) => label(i.ticket_type_id) },
    { header: "Assignees", width: 28, cell: (i) => names(i.assigned_to_id, personName) },
    {
      header: "Team",
      width: 18,
      cell: (i) => (i.assigned_team_id ? (teamName.get(i.assigned_team_id) ?? UNKNOWN) : null),
    },
    { header: "Tags", width: 20, cell: (i) => names(i.team_ids, teamName) },
    { header: "Departments", width: 28, cell: (i) => names(i.subsheet_ids, subsheetName) },
    { header: "Filed by", width: 20, cell: (i) => personName.get(i.added_by_id) ?? UNKNOWN },
    {
      header: "ETA",
      width: 12,
      format: "yyyy-mm-dd",
      cell: (i) => (i.eta_to_code_complete ? calendarDay(i.eta_to_code_complete) : null),
    },
    { header: "Created", width: 18, format: "yyyy-mm-dd hh:mm", cell: (i) => localWallClock(i.created_at) },
    { header: "Updated", width: 18, format: "yyyy-mm-dd hh:mm", cell: (i) => localWallClock(i.updated_at) },
    { header: "Private", width: 9, cell: (i) => (i.is_private ? "Yes" : "No") },
    { header: "Description", width: 60, cell: (i) => (i.description ? htmlToText(i.description) || null : null) },
    ...(lookups.customFields ?? []).map((field) => {
      const value = (i: BacklogItem) => i.custom_fields[field.id];
      if (field.field_type === "number")
        return {
          header: field.name,
          width: 12,
          cell: (i: BacklogItem) => (typeof value(i) === "number" ? (value(i) as number) : null),
        };
      if (field.field_type === "date")
        return {
          header: field.name,
          width: 12,
          format: "yyyy-mm-dd",
          // The database guarantees the "YYYY-MM-DD" shape (0064).
          cell: (i: BacklogItem) => {
            const v = value(i);
            return typeof v === "string" ? calendarDay(v) : null;
          },
        };
      return { header: field.name, width: 20, cell: (i: BacklogItem) => formatFieldValue(field, value(i)) };
    }),
  ];

  return {
    columns: columns.map(({ header, width, format }) => ({ header, width, format })),
    rows: items.map((item) => columns.map((c) => c.cell(item))),
  };
}

/** `backlog-<scope>-<local date>.xlsx`, safe on every OS. */
export function exportFileName(scopeName: string | null, now: Date): string {
  const slug =
    (scopeName ?? "all items")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "all-items";
  const pad = (n: number) => String(n).padStart(2, "0");
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return `backlog-${slug}-${day}.xlsx`;
}

/** Writes the sheet and hands it to the browser as a download. */
export async function downloadSheet(sheet: ExportSheet, fileName: string): Promise<void> {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const header = sheet.columns.map((c) => ({ value: c.header, fontWeight: "bold" as const }));
  const body = sheet.rows.map((row) =>
    row.map((value, i) => {
      if (value === null) return null;
      const format = sheet.columns[i].format;
      return format ? { value, format } : { value };
    }),
  );
  await writeXlsxFile([header, ...body], {
    sheet: "Backlog",
    columns: sheet.columns.map((c) => ({ width: c.width })),
    stickyRowsCount: 1,
  }).toFile(fileName);
}
